import { Controller } from "@hotwired/stimulus"
import { FPS, clamp, formatDuration } from "../utils/aoe2_time"

// Trim editor for the AOE2 clip review page.
//
// Drag handles + numeric inputs set trim start/end (seconds) and mirror them
// into the hidden form fields `aoe2_clip[trim_start_s]` / `aoe2_clip[trim_end_s]`.
// Invariant enforced on every write: 0 <= start < end <= duration.
//
// Talks to the root `aoe2-clip-review` controller purely via events:
//   - dispatches `clip:seek` { seconds } when a handle is grabbed / dragged
//   - listens for `clip:timeupdate` { seconds } to draw the playhead
//   - listens for `clip:serialize` to flush the hidden fields on submit
//
// All math lives in utils/aoe2_time — this controller only wires DOM to state.
const MIN_SPAN = 1 / FPS // start and end may never be closer than one frame

export default class extends Controller {
  static targets = [
    "startInput", "endInput", "startField", "endField",
    "startHandle", "endHandle", "track", "playhead", "spanLabel",
  ]
  static values = {
    duration: { type: Number, default: 0 },
    start: { type: Number, default: 0 },
    end: { type: Number, default: 0 },
  }

  connect() {
    if (this.endValue <= 0) this.endValue = this.durationValue
    // Only real user edits (numeric input change / handle drag) may rewrite the
    // hidden fields. On connect they stay exactly as the server rendered them
    // ("" for an untrimmed clip) so an untouched form round-trips to "no trim".
    this.dirty = false
    this.root = this.element.closest('[data-controller~="aoe2-clip-review"]') || this.element
    this.onTimeupdate = (event) => this.drawPlayhead(Number(event.detail && event.detail.seconds) || 0)
    this.onSerialize = () => this.writeFields()
    this.root.addEventListener("clip:timeupdate", this.onTimeupdate)
    this.root.addEventListener("clip:serialize", this.onSerialize)
    this.sync()
  }

  disconnect() {
    this.root.removeEventListener("clip:timeupdate", this.onTimeupdate)
    this.root.removeEventListener("clip:serialize", this.onSerialize)
    this.endDrag()
  }

  // --- numeric inputs -------------------------------------------------------

  startChanged() {
    this.dirty = true
    this.setStart(Number(this.startInputTarget.value))
  }

  endChanged() {
    this.dirty = true
    this.setEnd(Number(this.endInputTarget.value))
  }

  setStart(seconds) {
    if (!Number.isFinite(seconds)) return this.sync()
    this.startValue = clamp(0, this.endValue - MIN_SPAN, seconds)
    this.sync()
  }

  setEnd(seconds) {
    if (!Number.isFinite(seconds)) return this.sync()
    this.endValue = clamp(this.startValue + MIN_SPAN, this.durationValue, seconds)
    this.sync()
  }

  // --- trim handles ---------------------------------------------------------

  grabStart(event) {
    this.beginDrag(event, "start")
  }

  grabEnd(event) {
    this.beginDrag(event, "end")
  }

  beginDrag(event, which) {
    event.preventDefault()
    this.dragging = which
    this.seek(which === "start" ? this.startValue : this.endValue)
    this.onMove = (e) => this.drag(e)
    this.onUp = () => this.endDrag()
    window.addEventListener("pointermove", this.onMove)
    window.addEventListener("pointerup", this.onUp)
  }

  drag(event) {
    const seconds = this.pointerSeconds(event)
    if (seconds === null) return
    this.dirty = true
    if (this.dragging === "start") this.setStart(seconds)
    else this.setEnd(seconds)
    this.seek(this.dragging === "start" ? this.startValue : this.endValue)
  }

  endDrag() {
    if (this.onMove) window.removeEventListener("pointermove", this.onMove)
    if (this.onUp) window.removeEventListener("pointerup", this.onUp)
    this.onMove = this.onUp = null
    this.dragging = null
  }

  // Map a pointer x-position onto the trim track as seconds.
  pointerSeconds(event) {
    if (!this.hasTrackTarget || this.durationValue <= 0) return null
    const rect = this.trackTarget.getBoundingClientRect()
    if (rect.width <= 0) return null
    return ((event.clientX - rect.left) / rect.width) * this.durationValue
  }

  seek(seconds) {
    this.dispatch("seek", { prefix: "clip", bubbles: true, detail: { seconds } })
  }

  // --- rendering ------------------------------------------------------------

  drawPlayhead(seconds) {
    if (!this.hasPlayheadTarget || this.durationValue <= 0) return
    this.playheadTarget.style.left = `${clamp(0, 100, (seconds / this.durationValue) * 100)}%`
  }

  sync() {
    this.writeFields()
    this.reflectInput(this.hasStartInputTarget && this.startInputTarget, this.startValue)
    this.reflectInput(this.hasEndInputTarget && this.endInputTarget, this.endValue)
    this.positionHandle(this.hasStartHandleTarget && this.startHandleTarget, this.startValue)
    this.positionHandle(this.hasEndHandleTarget && this.endHandleTarget, this.endValue)
    if (this.hasSpanLabelTarget) {
      this.spanLabelTarget.textContent = formatDuration(this.endValue - this.startValue)
    }
  }

  writeFields() {
    // Never overwrite the server-rendered hidden fields until the curator has
    // actually edited the window — otherwise an untouched clip gets marked
    // trimmed 0..duration (or, on a fractional duration, an un-saveable span).
    if (!this.dirty) return

    // A full [0, duration] selection (within one frame) IS "no trim": write
    // empty strings so the save drops the trim keys and round-trips cleanly.
    if (this.isFullSpan()) {
      if (this.hasStartFieldTarget) this.startFieldTarget.value = ""
      if (this.hasEndFieldTarget) this.endFieldTarget.value = ""
      return
    }

    if (this.hasStartFieldTarget) this.startFieldTarget.value = this.startValue.toFixed(2)
    if (this.hasEndFieldTarget) this.endFieldTarget.value = this.endValue.toFixed(2)
  }

  isFullSpan() {
    return Math.abs(this.startValue) <= MIN_SPAN &&
      Math.abs(this.endValue - this.durationValue) <= MIN_SPAN
  }

  reflectInput(input, seconds) {
    if (input && document.activeElement !== input) input.value = seconds.toFixed(2)
  }

  positionHandle(handle, seconds) {
    if (handle && this.durationValue > 0) {
      handle.style.left = `${(seconds / this.durationValue) * 100}%`
    }
  }
}
